import { useRef, useState } from "react";
import { useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";

interface VideoItem {
  id: number;
  src: string;
  size?: "normal" | "wide";
}

const videos: VideoItem[] = [
  {
    id: 1,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/eb847b36-8960-42e0-aa2e-a61997048518_VID-20260421-WA0027.mp4?v=cc56dd9677a118ee671074aac67c5a0f",
    size: "wide",
  },
  {
    id: 2,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/aa4cf10c-3238-435a-94d2-83435eac21bc_VID-20260421-WA0031.mp4?v=a62864dbd040ce27eb3aa611e2ddf1df",
  },
  {
    id: 3,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/34f049e1-852f-4184-88a1-50f7daf2a0e4_VID-20260421-WA0026.mp4?v=feb2c3e38b0f98f0bfb441e2c58917ea",
  },
  {
    id: 4,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/ebf0606f-b9ac-4395-ab87-c6b842df145a_VID-20260421-WA0033.mp4?v=9bf4e71382e9b2f4202d5153961dff7c",
  },
  {
    id: 5,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/cb1ef9e8-9e48-4106-b0fa-c4c7f28c745f_VID-20260421-WA0025.mp4?v=aa31d86bac01bb6ca869afb6ade07121",
  },
  {
    id: 6,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/a1967d73-7ba9-4e27-b505-aed3bb265596_VIDEO-2026-04-21-14-29-59.mp4?v=b38d1d98821ff936814d6b5b3ddd61d3",
    size: "wide",
  },
  {
    id: 7,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/180fbb3c-ad74-4952-a9b8-304d2620b7c1_VIDEO-2026-04-21-14-28-43.mp4?v=e93b88fbd510f6d1cf42da8030d0d31b",
  },
  {
    id: 8,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/ad666850-fb20-4d5a-8392-1e0422846514_VIDEO-2026-04-21-14-28-45.mp4?v=2b9bfb148213f4c8e10cf649c6a5770c",
  },
  {
    id: 9,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/c819cc97-b4d4-417d-a6fa-170ddf42b83a_VIDEO-2026-04-21-14-28-46.mp4?v=2c525a8ae0dcf594f49fcad4718c8d79",
  },
  {
    id: 10,
    src: "https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/a8c983a9-52cd-4572-9815-c56f3531d2e9_VIDEO-2026-04-21-14-28-41.mp4?v=f17dad13006766e869b30d88861bca04",
  },
];

function VideoCard({ video, idx, visibleItems, setRef }: {
  video: VideoItem;
  idx: number;
  visibleItems: boolean[];
  setRef: (idx: number) => (el: HTMLElement | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={setRef(idx) as (el: HTMLDivElement | null) => void}
      className={`group relative overflow-hidden rounded-2xl border border-gray-100 reveal reveal-delay-${Math.min(idx + 1, 6)} ${visibleItems[idx] ? "visible" : ""} ${video.size === "wide" ? "lg:col-span-2" : ""}`}
    >
      <div
        className={`relative w-full overflow-hidden ${video.size === "wide" ? "h-64 md:h-72" : "h-56 md:h-64"}`}
        onClick={handleVideoClick}
      >
        <video
          ref={videoRef}
          src={video.src}
          className="w-full h-full object-cover object-center"
          playsInline
          preload="metadata"
          controls={playing}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />

        {/* Play overlay — only shown when not playing */}
        {!playing && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4A]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              aria-label="Play video"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm border-2 border-white/60 rounded-full group-hover:bg-[#7FBF3F]/80 group-hover:border-[#7FBF3F] transition-all duration-300">
                <i className="ri-play-fill text-white text-2xl ml-1"></i>
              </div>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function VideoGallery() {
  const { setRef, visibleItems } = useScrollAnimationMultiple(videos.length);

  return (
    <section id="video-gallery" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header — matches GalleryGrid style */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-[#7FBF3F]/20 text-[#7FBF3F] text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
            <i className="ri-video-line"></i>Video Gallery
          </div>
          <h2
            className="font-serif text-3xl md:text-4xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            See Our Projects in Action
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
            Real site footage and walkthroughs from our residential developments on Sarjapur Road, Bengaluru.
          </p>
        </div>

        {/* Video Grid — identical layout to image grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video, idx) => (
            <VideoCard
              key={video.id}
              video={video}
              idx={idx}
              visibleItems={visibleItems}
              setRef={setRef}
            />
          ))}
        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          Showing {videos.length} project videos
        </p>
      </div>
    </section>
  );
}
